import {
  determineFirstPathYear,
  pathToYearValueMap,
  pathValuesFromYear,
  TARGET_PATH_LAST_YEAR,
  TARGET_PATH_YEARS_RANGE,
  toStandardPathLength,
} from '@predium/client-lookup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isNil } from 'lodash';
import { roundDate } from '../date/date';

dayjs.extend(utc);

/**
 * Returns the stranding year ranges of a building or multiple buildings.
 * TargetPaths or actualPaths could be for one building or an aggregation of multiple buildings
 * A stranding year happens when the value of the actual path is less than the target path
 * @param path
 * @param targetPath
 * @param constructionYear construction year of a building, or earliest construction year in case of multiple building
 * @returns array of stranding ranges, the first object should be the earliest after the construction year
 * The ranges are inclusive (startYear and endYear are in the stranding period)
 */
export const getStrandingYearRanges = ({
  path,
  targetPath,
  constructionYear,
}: {
  path: { year: number; value: number }[];
  targetPath: { year: number; value: number }[];
  constructionYear: number;
}): { startYear: number; endYear: number }[] => {
  const adjustedToConstructionYearTargetPath = pathValuesFromYear(
    TARGET_PATH_YEARS_RANGE,
    constructionYear,
  ) as number[];

  // -------- validation steps --------
  if (path.length === 0) {
    console.error('Error: Stranding Year Ranges: Path length is 0');
    return [];
  }
  if (targetPath.length === 0) {
    console.error('Error: Stranding Year Ranges: Target Path length is 0');
    return [];
  }
  if (
    path.length !== adjustedToConstructionYearTargetPath.length ||
    targetPath.length !== adjustedToConstructionYearTargetPath.length
  ) {
    console.error('Error: Stranding Year Ranges: Target paths and actual paths are not of the same length');
    return [];
  }

  if (adjustedToConstructionYearTargetPath[0] !== path[0].year) {
    console.error(
      `Error: Stranding Year Ranges: Actual path first year is not matching: ${adjustedToConstructionYearTargetPath[0]} !== ${path[0].year}`,
    );
    return [];
  }

  if (adjustedToConstructionYearTargetPath[0] !== targetPath[0].year) {
    console.error(
      `Error: Stranding Year Ranges: Target path first year is not matching: ${adjustedToConstructionYearTargetPath[0]} !== ${targetPath[0].year}`,
    );
    return [];
  }

  if (
    adjustedToConstructionYearTargetPath.some((year, index) => year !== path[index].year) ||
    adjustedToConstructionYearTargetPath.some((year, index) => year !== targetPath[index].year)
  ) {
    console.error(`Error: Stranding Year Ranges: Target path and actual path orders do not match `);
    return [];
  }

  if (constructionYear > adjustedToConstructionYearTargetPath[adjustedToConstructionYearTargetPath.length - 1]) {
    // Construction year comes after the range, It is probably an invalid case
    console.error('Error: Stranding Year Ranges: Construction year comes after the target path years range');
    return [];
  }

  //--------  Generate stranding year ranges   --------
  // stranding years can only occur after the construction year, so we start iteration from construction year
  // or if it is before the target path years range, then we take the first year in the range
  let yearIndexIterator =
    constructionYear < adjustedToConstructionYearTargetPath[0]
      ? 0
      : adjustedToConstructionYearTargetPath.indexOf(constructionYear);
  const strandingYearRanges = [];
  for (yearIndexIterator; yearIndexIterator < adjustedToConstructionYearTargetPath.length; yearIndexIterator += 1) {
    const currentStrandingStartYear = adjustedToConstructionYearTargetPath[yearIndexIterator];
    let currentStrandingEndYear = null;

    while (
      yearIndexIterator < adjustedToConstructionYearTargetPath.length &&
      path[yearIndexIterator].value >= targetPath[yearIndexIterator].value
    ) {
      // A stranding year is found, so we keep iterating until we reach the end of the stranding period
      currentStrandingEndYear = adjustedToConstructionYearTargetPath[yearIndexIterator];
      yearIndexIterator += 1;
    }

    // Either, there is a stranding range from the previous while loop, which will change `currentStrandingEndYear`
    // from null to a value so will enter the condition or no stranding years so far and nothing happen
    if (currentStrandingEndYear !== null) {
      strandingYearRanges.push({
        startYear: currentStrandingStartYear,
        endYear:
          currentStrandingEndYear === TARGET_PATH_LAST_YEAR ? currentStrandingEndYear : currentStrandingEndYear + 1,
      });
    }
  }
  return strandingYearRanges;
};

export const getStrandingYearRangesFromValues = ({
  targetPath,
  path,
  constructionYear,
}: {
  targetPath: number[];
  path: number[];
  constructionYear: number | undefined;
}): { startYear: number; endYear: number }[] => {
  if (isNil(constructionYear)) return [];

  const adjustedToConstructionYearPath = pathValuesFromYear(
    toStandardPathLength(path, Number.MIN_SAFE_INTEGER),
    determineFirstPathYear(constructionYear),
  );
  const adjustedToConstructionYearTargetPath = pathValuesFromYear(
    toStandardPathLength(targetPath, Number.MAX_SAFE_INTEGER),
    determineFirstPathYear(constructionYear),
  );

  return getStrandingYearRanges({
    targetPath: pathToYearValueMap(adjustedToConstructionYearTargetPath, constructionYear),
    path: pathToYearValueMap(adjustedToConstructionYearPath, constructionYear),
    constructionYear,
  });
};

export const getFirstStrandingYear = ({
  targetPath,
  path,
  constructionYear,
}: {
  targetPath: number[];
  path: number[];
  constructionYear: number;
}): number | undefined => {
  const strandingDateRanges = getStrandingYearRangesFromValues({
    targetPath,
    path,
    constructionYear,
  });
  if (strandingDateRanges.length === 0) {
    return undefined;
  } else {
    return strandingDateRanges[0].startYear;
  }
};

export const getFirstStrandingDate = ({
  targetPath,
  path,
  constructionYear,
}: {
  targetPath: number[];
  path: number[];
  constructionYear: number;
}): Date | undefined => {
  const firstStrandingYear = getFirstStrandingYear({
    targetPath,
    path,
    constructionYear,
  });
  return firstStrandingYear ? new Date(firstStrandingYear, 0, 1) : undefined;
};

/**
 * Computes the stranding date of a building after implementing an action. Note
 * that this is slightly different from calculating the stranding date of a
 * building. If the stranding date after implementing an action is the same as
 * the implementation date, this means that the action does not improve the stranding date.
 * Nevertheless, the stranding date can be worse than the previousStranding
 * */
export const getActionStrandingDate = ({
  implementationTo,
  targetPath,
  path,
  previousStrandingDate,
  constructionYear,
}: {
  implementationTo: Date;
  targetPath: number[];
  path: number[];
  previousStrandingDate: Date | undefined;
  constructionYear: number;
}): Date | undefined => {
  const roundedImplementationYear = roundDate(dayjs.utc(implementationTo)).year();

  const strandingYearRanges = getStrandingYearRangesFromValues({
    constructionYear,
    targetPath,
    path,
  });

  const strandingDatesAfterImplementation = strandingYearRanges.filter(
    (strandingRange) =>
      (roundedImplementationYear >= strandingRange.startYear && roundedImplementationYear < strandingRange.endYear) ||
      strandingRange.startYear >= roundedImplementationYear,
  );
  const firstStrandingRangeAfterImplementation = strandingDatesAfterImplementation.at(0);

  if (firstStrandingRangeAfterImplementation === undefined) {
    return undefined;
  }

  const strandingDate = new Date(
    Math.max(firstStrandingRangeAfterImplementation.startYear, roundedImplementationYear),
    0,
    1,
  );
  if (previousStrandingDate === undefined) {
    return strandingDate;
  }

  if (
    strandingDate.getFullYear() === roundedImplementationYear &&
    previousStrandingDate.getFullYear() < strandingDate.getFullYear()
  ) {
    return previousStrandingDate;
  }
  return strandingDate;
};

/**
 * Computes the stranding date of a building after implementing all the actions. Note
 * that this is slightly different from calculating the stranding date of a
 * building. If the stranding date after implementing an action is the same as
 * the implementation date, this means that the action has no impact on the
 * stranding date, and thus the previous stranding date is returned. If the
 * total path is stranded at multiple points, the stranding date returned refers
 * to the first stranding after the last action.
 * */
export const getActionsStrandingDate = ({
  targetPath,
  building,
  actions,
}: {
  actions: { implementationTo: Date; path: number[] }[];
  building: { path: number[]; constructionYear: number };
  targetPath: number[];
}): Date | undefined => {
  const buildingStrandingDate = getFirstStrandingDate({
    constructionYear: building.constructionYear,
    targetPath,
    path: building.path,
  });

  return actions.reduce(
    (previousStrandingDate, { implementationTo, path }) =>
      getActionStrandingDate({
        implementationTo: roundDate(dayjs.utc(implementationTo)).toDate(),
        targetPath,
        path,
        previousStrandingDate,
        constructionYear: building.constructionYear,
      }),
    buildingStrandingDate,
  );
};
